import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Loader, Gift, User, Eye, EyeOff, ChevronDown, Check, X } from 'lucide-react';
import AuthSplitLayout from './AuthSplitLayout';
import AuthInstallWidget from './AuthInstallWidget';
import './SimpleRegister.css';

// Backend password rules (mirror backend/utils/security.js validatePassword)
const PASSWORD_SPECIAL_CHARS_RE = /[@$!%*?&#+=\-_.,;:]/;
const COMMON_WEAK_PATTERNS = [
  'password', '12345678', 'qwerty', 'admin', 'letmein',
  'welcome', 'monkey', 'dragon', 'master', 'abc123'
];

const evaluatePassword = (password) => {
  const pwd = password || '';
  const lower = pwd.toLowerCase();
  return {
    minLength: pwd.length >= 8,
    hasLower: /[a-z]/.test(pwd),
    hasUpper: /[A-Z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
    hasSpecial: PASSWORD_SPECIAL_CHARS_RE.test(pwd),
    notCommon: pwd.length === 0 ? true : !COMMON_WEAK_PATTERNS.some((w) => lower.includes(w))
  };
};

const SimpleRegister = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Trial signup detection from URL (?plan=PREMIUM&trial=true)
  const planParam = searchParams.get('plan')?.toUpperCase() || null;
  const trialParam = searchParams.get('trial') === 'true';
  const isTrialSignup = trialParam && planParam && planParam !== 'FREE';

  // Referral code: URL takes priority (auto-fill + auto-expand)
  const urlReferralCode = searchParams.get('ref') || '';
  const [manualReferralCode, setManualReferralCode] = useState('');
  const [referralExpanded, setReferralExpanded] = useState(!!urlReferralCode);
  const [referralValid, setReferralValid] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  const effectiveReferralCode = urlReferralCode || manualReferralCode;

  // Form state — single page, no two-step
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Live validations
  const passwordRules = useMemo(() => evaluatePassword(formData.password), [formData.password]);
  const passwordValid = Object.values(passwordRules).every(Boolean);
  const passwordsMatch =
    formData.password.length > 0 && formData.password === formData.confirmPassword;
  const passwordsTouchedAndMismatch =
    formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;

  const canSubmit =
    formData.fullName.trim().length > 0 &&
    formData.email.trim().length > 0 &&
    formData.phone.trim().length > 0 &&
    passwordValid &&
    passwordsMatch &&
    agreedToTerms &&
    !loading;

  // Validate URL referral on mount
  useEffect(() => {
    if (urlReferralCode) {
      validateReferralCode(urlReferralCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlReferralCode]);

  // Debounced validation for manual referral code
  useEffect(() => {
    if (urlReferralCode || !manualReferralCode) return undefined;
    const timer = setTimeout(() => validateReferralCode(manualReferralCode), 500);
    return () => clearTimeout(timer);
  }, [manualReferralCode, urlReferralCode]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(false);
      setReferrerName('');
      return;
    }
    setValidatingReferral(true);
    try {
      const res = await fetch(`/api/referral/validate/${code}`);
      const data = await res.json();
      if (data.success) {
        setReferralValid(true);
        setReferrerName(data.referrer?.name || 'a friend');
      } else {
        setReferralValid(false);
        setReferrerName('');
      }
    } catch {
      setReferralValid(false);
      setReferrerName('');
    } finally {
      setValidatingReferral(false);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Phone: digits only, strip prefixes, max 10
    if (name === 'phone') {
      value = value.replace(/\D/g, '');
      if (value.startsWith('234') && value.length > 10) value = value.substring(3);
      if (value.startsWith('0')) value = value.substring(1);
      value = value.substring(0, 10);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) return;

    // Split full name client-side. Backend still receives firstName/lastName.
    const trimmed = formData.fullName.trim().replace(/\s+/g, ' ');
    const parts = trimmed.split(' ');
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || null;

    setLoading(true);
    try {
      const res = await fetch('/api/merchant/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email.trim().toLowerCase(),
          phone: `+234${formData.phone}`,
          password: formData.password,
          role: 'MERCHANT',
          agreedToTerms: true,
          startTrial: isTrialSignup,
          trialTier: isTrialSignup ? planParam : null,
          referralCode: referralValid ? effectiveReferralCode : null
        })
      });

      const data = await res.json();

      if (data.success) {
        if (data.requiresOTP) {
          navigate(`/verify-otp?email=${encodeURIComponent(formData.email.trim())}`, {
            state: {
              newUser: true,
              isOnTrial: data.isOnTrial || false,
              trialTier: data.trialTier || null,
              trialDays: data.trialDays || null
            }
          });
        } else {
          // Defensive fallback (current backend always returns requiresOTP:true)
          navigate('/onboarding');
        }
      } else {
        setError(data.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout>
      <div className="srx-container">
      <div className="srx-content">
        <div className="srx-header">
          <img src="/logo192.png" alt="Revstar" className="srx-logo" />
          <h1 className="srx-title">
            {isTrialSignup ? `Start your ${planParam} free trial` : 'Create your free account'}
          </h1>
          <p className="srx-subtitle">
            {isTrialSignup
              ? '7 days free. No credit card required.'
              : 'Set up your store in minutes. No credit card required.'}
          </p>
        </div>

        {isTrialSignup && (
          <div className="srx-banner srx-banner-trial" role="note">
            <Gift size={16} aria-hidden="true" />
            <span>{`7-day ${planParam} trial — full access to ${planParam.toLowerCase()} features`}</span>
          </div>
        )}

        {referralValid && referrerName && (
          <div className="srx-banner srx-banner-referral" role="note">
            <Gift size={16} aria-hidden="true" />
            <span>Referred by {referrerName}</span>
          </div>
        )}

        <form className="srx-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="srx-error" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <label className="srx-field">
            <span className="srx-label">Full name</span>
            <span className="srx-input-wrap">
              <span className="srx-input-icon" aria-hidden="true">
                <User size={18} />
              </span>
              <input
                type="text"
                name="fullName"
                placeholder="Your full name"
                value={formData.fullName}
                onChange={handleChange}
                disabled={loading}
                autoComplete="name"
                required
                className="srx-input"
              />
            </span>
          </label>

          <label className="srx-field">
            <span className="srx-label">Email</span>
            <span className="srx-input-wrap">
              <span className="srx-input-icon" aria-hidden="true">
                <Mail size={18} />
              </span>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                autoComplete="email"
                inputMode="email"
                required
                className="srx-input"
              />
            </span>
          </label>

          <label className="srx-field">
            <span className="srx-label">Phone</span>
            <span className="srx-input-wrap srx-input-wrap-phone">
              <span className="srx-phone-prefix" aria-hidden="true">
                <span className="srx-phone-flag">🇳🇬</span>
                <span className="srx-phone-code">+234</span>
              </span>
              <input
                type="tel"
                name="phone"
                placeholder="801 234 5678"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                autoComplete="tel-national"
                inputMode="numeric"
                required
                className="srx-input srx-input-phone"
              />
            </span>
          </label>

          <label className="srx-field">
            <span className="srx-label">Password</span>
            <span className="srx-input-wrap">
              <span className="srx-input-icon" aria-hidden="true">
                <Lock size={18} />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                autoComplete="new-password"
                required
                className="srx-input srx-input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="srx-eye"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
          </label>

          {formData.password.length > 0 && (
            <ul className="srx-rules" aria-label="Password requirements">
              <PasswordRule met={passwordRules.minLength} label="At least 8 characters" />
              <PasswordRule met={passwordRules.hasLower} label="One lowercase letter" />
              <PasswordRule met={passwordRules.hasUpper} label="One uppercase letter" />
              <PasswordRule met={passwordRules.hasNumber} label="One number" />
              <PasswordRule met={passwordRules.hasSpecial} label="One special character" />
              {!passwordRules.notCommon && (
                <PasswordRule met={false} label="Avoid common patterns" />
              )}
            </ul>
          )}

          <label className="srx-field">
            <span className="srx-label">Confirm password</span>
            <span className="srx-input-wrap">
              <span className="srx-input-icon" aria-hidden="true">
                <Lock size={18} />
              </span>
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                autoComplete="new-password"
                required
                className="srx-input srx-input-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="srx-eye"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </span>
            {passwordsMatch && (
              <span className="srx-match srx-match-ok">
                <Check size={14} aria-hidden="true" /> Passwords match
              </span>
            )}
            {passwordsTouchedAndMismatch && (
              <span className="srx-match srx-match-bad">
                <X size={14} aria-hidden="true" /> Passwords don't match
              </span>
            )}
          </label>

          {/* Referral code — collapsed by default, auto-expanded when ?ref= present */}
          {!referralExpanded && !urlReferralCode && (
            <button
              type="button"
              className="srx-referral-toggle"
              onClick={() => setReferralExpanded(true)}
            >
              <Gift size={14} aria-hidden="true" />
              <span>Have a referral code?</span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
          )}

          {(referralExpanded || urlReferralCode) && (
            <label className="srx-field">
              <span className="srx-label">Referral code</span>
              <span className="srx-input-wrap">
                <span className="srx-input-icon" aria-hidden="true">
                  <Gift size={18} />
                </span>
                <input
                  type="text"
                  name="referralCode"
                  placeholder="MER-XXXX"
                  value={urlReferralCode || manualReferralCode}
                  onChange={(e) => setManualReferralCode(e.target.value.toUpperCase())}
                  disabled={loading || !!urlReferralCode}
                  className="srx-input"
                />
                {validatingReferral && (
                  <span className="srx-referral-status srx-referral-checking">
                    <Loader size={14} className="srx-spin" aria-hidden="true" />
                  </span>
                )}
                {!validatingReferral && effectiveReferralCode && referralValid && (
                  <span className="srx-referral-status srx-referral-ok" aria-label="Valid referral code">
                    <Check size={14} aria-hidden="true" />
                  </span>
                )}
                {!validatingReferral && effectiveReferralCode && !referralValid && effectiveReferralCode.length >= 4 && (
                  <span className="srx-referral-status srx-referral-bad" aria-label="Invalid referral code">
                    <X size={14} aria-hidden="true" />
                  </span>
                )}
              </span>
            </label>
          )}

          <label className="srx-terms">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              disabled={loading}
            />
            <span className="srx-terms-box" aria-hidden="true">
              <Check size={12} strokeWidth={3} />
            </span>
            <span className="srx-terms-text">
              I agree to the{' '}
              <a href="https://userevstar.com/terms" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </a>{' '}
              and{' '}
              <a href="https://userevstar.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </span>
          </label>

          <button
            type="submit"
            className="srx-cta"
            disabled={!canSubmit}
          >
            {loading ? (
              <>
                <Loader size={18} className="srx-spin" aria-hidden="true" />
                <span>{isTrialSignup ? 'Starting trial...' : 'Creating account...'}</span>
              </>
            ) : (
              <span>{isTrialSignup ? `Start ${planParam} trial` : 'Create free account'}</span>
            )}
          </button>

          <p className="srx-footer">
            Already have an account?{' '}
            <Link to="/login" className="srx-footer-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
      </div>
      <AuthInstallWidget />
    </AuthSplitLayout>
  );
};

const PasswordRule = ({ met, label }) => (
  <li className={`srx-rule ${met ? 'srx-rule-ok' : 'srx-rule-pending'}`}>
    <span className="srx-rule-icon" aria-hidden="true">
      {met ? <Check size={12} strokeWidth={3} /> : <span className="srx-rule-dot" />}
    </span>
    <span>{label}</span>
  </li>
);

export default SimpleRegister;
